import "styles/detailSubmission.less";

import { getCurrentTaxYear } from "constants/taxYears";

import React, { Dispatch, SetStateAction, useContext, useState } from "react";

import { Select, InputNumber, Flex, Typography, Button, SelectProps } from "antd";
import { AppContext } from "App";
import pensionContributionPopup from "assets/images/new-questionnaire/general/pension-contribution.png";
import PromptModal from "components/pages/Questionnaire/components/Shared/PromptModal";
import { PromptModalType } from "enums/questionnaire";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import { useAppSelector } from "redux/hooks";
import { RootState } from "redux/store";
import { cleanHTMLString } from "utils/strings";

type DetailSubmissionComponentProps = {
    setShowResults: Dispatch<SetStateAction<boolean>>;
};

const DetailSubmission: React.FC<DetailSubmissionComponentProps> = ({ setShowResults }) => {
    const { isMobile } = useContext(AppContext);
    const taxYearOptions: SelectProps["options"] = [];
    const { taxYears } = useAppSelector((state: RootState) => ({
        taxYears: state.taxYears.taxYears,
    }));
    const currentTaxYear = getCurrentTaxYear(taxYears);
    for (let i = 0; i < taxYears.length; i++) {
        taxYearOptions.push({
            label: taxYears[i].year,
            value: taxYears[i].year,
        });
    }
    const [ taxYear, setTaxYear ] = useState<string>(getCurrentTaxYear(taxYears));
    const [ country, setCountry ] = useState<string>("england");
    const [ grossIncome, setGrossIncome ] = useState<number | null>(null);
    const [ hasStudentLoans, setHasStudentLoans ] = useState<boolean | null>(null);
    const [ hasFinishedBeforeApril, setHasFinishedBeforeApril ] = useState<boolean | null>(null);
    const [ repaymentPlan, setRepaymentPlan ] = useState<string | null>("plan1");
    const [ makesPensionContribution, setMakesPensionContribution ] = useState<boolean | null>(null);
    const [ hasWorkplacePension, setHasWorkplacePension ] = useState<boolean | null>(null);
    const [ pensionBeforeTax, setPensionBeforeTax ] = useState<boolean | null>(null);
    const [ pensionAmount, setPensionAmount ] = useState<number | null>(null);
    const {
        isOpened: isNetPayReliefModalOpened,
        open: handleNetPayReliefOpenModal,
        close: handleNetPayReliefCloseModal,
    } = useModalOpener();
    const {
        isOpened: isPensionTypeModalOpened,
        open: handlePensionTypeOpenModal,
        close: handlePensionTypeCloseModal,
    } = useModalOpener();
    const pensionTypeModalData = {
        type: PromptModalType.info,
        image: pensionContributionPopup,
        title: "Workplace Pension Vs Private Pension (SIPP)",
        text: cleanHTMLString(`<div style="color: #707070">
            <strong>A Workplace Pension </strong>
            <span>is arranged by your employer, where contributions are automatically deducted from your salary and often matched by your employer. It can operate under a Net Pay or Relief at Source arrangement, offering tax-efficient retirement savings.</span><br/>
            <strong>A Private Pension (SIPP) </strong>
            <span>is a type of private pension you manage yourself, offering more investment flexibility. Contributions receive tax relief, and it's suitable for those wanting control over how their pension is invested.</span> 
        </div>`),
        depend: [],
        manualOpen: true,
    };
    const netPayReliefModalData = {
        type: PromptModalType.info,
        image: pensionContributionPopup,
        title: "Net Pay Agreement Vs Relief at Source",
        text: cleanHTMLString(`<div style="color: #707070">
            <strong>Net Pay Agreement </strong>
            <span>deducts pension contributions from gross salary before tax, automatically reducing taxable income and providing tax relief, commonly used for workplace pensions.</span><br/>
            <strong>Relief at Source </strong>
            <span>deducts contributions from net salary and applies basic-rate tax relief, with higher-rate taxpayers needing to claim additional relief through their tax return. The main difference is that Net Pay offers automatic relief before tax, while Relief at Source requires the pension provider to claim relief, with higher-rate taxpayers needing to take extra steps.</span> 
        </div>`),
        depend: [],
        manualOpen: true,
    };
    const submitDetails = async () => {
        setShowResults(true);
    };
    const isFormValid =
        grossIncome !== null &&
        taxYear !== "" &&
        hasStudentLoans !== null &&
        (!hasStudentLoans ||
            (hasStudentLoans && hasFinishedBeforeApril !== null) ||
            (hasStudentLoans && hasFinishedBeforeApril && repaymentPlan !== null)) &&
        country !== "" &&
        makesPensionContribution !== null &&
        (!makesPensionContribution ||
            (makesPensionContribution && hasWorkplacePension === false && pensionAmount !== null) ||
            (makesPensionContribution && hasWorkplacePension && pensionBeforeTax !== null && pensionAmount !== null));
    const handleButtonClick = (question: string, selection: boolean) => {
        switch (question) {
        case "hasLoan":
            setHasStudentLoans(selection);
            if (!selection)
                setHasFinishedBeforeApril(null);
            break;
        case "makesPension":
            setMakesPensionContribution(selection);
            if (!selection) {
                setHasWorkplacePension(null);
                setPensionAmount(null);
            }
            break;
        case "finishedBeforeApril":
            setHasFinishedBeforeApril(selection);
            break;
        case "hasWorkplacePension":
            setHasWorkplacePension(selection);
            if (!selection)
                setPensionBeforeTax(null);
            break;
        case "pensionBeforeTax":
            setPensionBeforeTax(selection);
            break;
        }
    };

    return (
        <div className={ isMobile ? "detail-submission__container-mobile" : "detail-submission__container-desktop" }>
            <Typography className={ "detail-submission__header" }>
                Your Employment Details
            </Typography>
            <Typography className={ "detail-submission__body-text" }>
                Tax Year
            </Typography>
            <Select
                defaultValue={currentTaxYear}
                value={taxYear}
                options={taxYearOptions}
                onChange={value => setTaxYear(value)}
                className={"detail-submission__select"}
            />
            <Typography className={ "detail-submission__body-text" }>
                Country
            </Typography>
            <Select
                defaultValue={"england"}
                value={country}
                onChange={value => setCountry(value)}
                options={[
                    { value: "england", label: "England / Wales / Northern Ireland" },
                    { value: "scotland", label: "Scotland" },
                ]}
                className={"detail-submission__select"}
            />
            <Typography className={ "detail-submission__body-text" }>
                Gross Income
            </Typography>
            <InputNumber prefix="£" value={grossIncome} onChange={value => setGrossIncome(value)} className={ "detail-submission__input" } controls={false}/>
            <Typography className={ "detail-submission__body-text" }>
                Do you have student loan?
            </Typography>
            <Flex justify="space-between" align="center" className="detail-submission__binary-group">
                <Button
                    onClick={ () => handleButtonClick("hasLoan", true) }
                    className={ hasStudentLoans === true ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                >
                    Yes
                </Button>
                <Button
                    onClick={ () => handleButtonClick("hasLoan", false) }
                    className={ hasStudentLoans === false ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                >
                    No
                </Button>
            </Flex>
            {hasStudentLoans && (
                <div>
                    <Typography className={ "detail-submission__body-text" }>
                        Did you graduate or finish your course of study by the 5th April 2025?
                    </Typography>
                    <Flex justify="space-between" align="center" className="detail-submission__binary-group">
                        <Button
                            onClick={ () => handleButtonClick("finishedBeforeApril", true) }
                            className={ hasFinishedBeforeApril === true ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                        >
                            Yes
                        </Button>
                        <Button
                            onClick={ () => handleButtonClick("finishedBeforeApril", false) }
                            className={ hasFinishedBeforeApril === false ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button"}
                        >
                            No
                        </Button>
                    </Flex>
                    {hasFinishedBeforeApril && (
                        <div>
                            <Typography className={ "detail-submission__body-text" }>
                                Repayment plan you are currently on
                            </Typography>
                            <Select
                                defaultValue={"plan1"}
                                value={repaymentPlan}
                                onChange={value => setRepaymentPlan(value)}
                                options={[
                                    { value: "plan1", label: "Plan 1" },
                                    { value: "plan2", label: "Plan 2" },
                                    { value: "plan4", label: "Plan 4" },
                                    { value: "postGrad", label: "Postgraduate Loan" },
                                ]}
                                className={"detail-submission__select"}
                            />
                        </div>
                    )}
                </div>
            )}
            <Typography className={ "detail-submission__body-text" }>
                Do you make pension contributions?
            </Typography>
            <Flex justify="space-between" align="center" className="detail-submission__binary-group">
                <Button
                    onClick={ () => handleButtonClick("makesPension", true) }
                    className={ makesPensionContribution === true ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                >
                    Yes
                </Button>
                <Button
                    onClick={ () => handleButtonClick("makesPension", false) }
                    className={ makesPensionContribution === false ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                >
                    No
                </Button>
            </Flex>
            {makesPensionContribution && (
                <div>
                    <Typography className={ "detail-submission__body-text" }>
                        What type of pension contribution is this?
                    </Typography>
                    <span className="shared__learn-more detail-submission__learn-more" onClick={handlePensionTypeOpenModal}>
                        Learn more
                    </span>
                    {isPensionTypeModalOpened && (
                        <PromptModal
                            isOpen={isPensionTypeModalOpened}
                            closeModal={handlePensionTypeCloseModal}
                            handlePrev={() => {}}
                            data={pensionTypeModalData}
                        />
                    )}
                    <Flex justify="space-between" align="center" className="detail-submission__binary-group">
                        <Button
                            onClick={ () => handleButtonClick("hasWorkplacePension", true) }
                            className={ hasWorkplacePension === true ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                        >
                            Workplace Pension
                        </Button>
                        <Button
                            onClick={ () => handleButtonClick("hasWorkplacePension", false) }
                            className={ hasWorkplacePension === false ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                        >
                            Private Pension (SIPP)
                        </Button>
                    </Flex>
                    {hasWorkplacePension && (
                        <div>
                            <Typography className={ "detail-submission__body-text" }>
                                Were pension contributions made before tax (net pay agreement) or after tax (relief at source)?
                            </Typography>
                            <span className="shared__learn-more detail-submission__learn-more" onClick={handleNetPayReliefOpenModal}>
                                Learn more
                            </span>
                            {isNetPayReliefModalOpened && (
                                <PromptModal
                                    isOpen={isNetPayReliefModalOpened}
                                    closeModal={handleNetPayReliefCloseModal}
                                    handlePrev={() => {}}
                                    data={netPayReliefModalData}
                                />
                            )}
                            <Flex justify="space-between" align="center" className="detail-submission__binary-group">
                                <Button
                                    onClick={ () => handleButtonClick("pensionBeforeTax", true) }
                                    className={ pensionBeforeTax === true ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                                >
                                    Before Tax (Net Pay Agreement)
                                </Button>
                                <Button
                                    onClick={ () => handleButtonClick("pensionBeforeTax", false) }
                                    className={ pensionBeforeTax === false ? "detail-submission__binary-button-confirmed" : "detail-submission__binary-button" }
                                >
                                    After Tax (Relief at Source)
                                </Button>
                            </Flex>
                        </div>
                    )}
                    <Typography className={ "detail-submission__body-text" }>
                        How much do you contribute to your pension?
                    </Typography>
                    <InputNumber prefix="£" value={pensionAmount} onChange={value => setPensionAmount(value)} className={ "detail-submission__input" } controls={false}/>
                </div>
            )}
            <Button type="primary" disabled={!isFormValid} onClick={submitDetails} className="shared__primary-button detail-submission__calc-button-width-100">Calculate</Button>
        </div>
    );
};

export default DetailSubmission;
